import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, GradButton, TIP_ETIKET } from "@/components/erp";
import { SelectBox, DynamicField } from "@/components/Inputs";
import { RotaSecici } from "@/components/IsEmriDialog";
import api, { API, errMsg } from "@/lib/api";

const BOS = { kod: "", ad: "", barkod: "", ek_birimler: [], etiketler: "", ihracat: {}, kategori: "", kategori_tipi: "mamul", birim: "adet", para_birimi: "TRY", kdv_orani: 20, stok_takibi: true,
  min_stok: 0, alis_fiyati: 0, operasyon_maliyet: 0, satis_fiyati: 0, perakende_fiyati: 0, satin_aliyoruz: false, ambalaj_carpanlari: {}, ozellikler: {}, rota: [], aciklama: "", aktif: true };

export const IHRACAT = [["gtip", "GTİP kodu", "4009.31.00.00.00"], ["mensei", "Menşei ülke", "Türkiye"], ["ingilizce_ad", "İngilizce ürün adı", ""], ["net_kg", "Net ağırlık (kg)", ""],
  ["brut_kg", "Brüt ağırlık (kg)", ""], ["en_cm", "En (cm)", ""], ["boy_cm", "Boy (cm)", ""], ["yukseklik_cm", "Yükseklik (cm)", ""]];

const EkBirimler = ({ form, set, birimler }) => {
  const l = form.ek_birimler || [];
  const guncelle = (i, p) => set("ek_birimler", l.map((x, n) => (n === i ? { ...x, ...p } : x)));
  return (
    <div className="mt-2" data-testid="urun-ek-birimler">
      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Çoklu birim (örn. 1 koli = 24 {form.birim})</div>
      {l.map((b, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 mb-2 items-center">
          <SelectBox testid={`urun-ek-birim-${i}`} value={b.birim} onChange={(v) => guncelle(i, { birim: v })} options={[...new Set([...birimler, "koli", "paket", "kutu", "palet", b.birim].filter(Boolean))]} />
          <Input data-testid={`urun-ek-carpan-${i}`} type="number" step="any" placeholder={`kaç ${form.birim}`} className="bg-white/5" value={b.carpan} onChange={(e) => guncelle(i, { carpan: Number(e.target.value) })} />
          <Input data-testid={`urun-ek-barkod-${i}`} placeholder="Barkod (isteğe bağlı)" className="bg-white/5" value={b.barkod || ""} onChange={(e) => guncelle(i, { barkod: e.target.value })} />
          <button type="button" data-testid={`urun-ek-sil-${i}`} onClick={() => set("ek_birimler", l.filter((_, n) => n !== i))} className="p-2 rounded-lg text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
        </div>))}
      <button type="button" data-testid="urun-ek-birim-ekle" onClick={() => set("ek_birimler", [...l, { birim: "koli", carpan: 1, barkod: "" }])} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs border border-white/15 hover:bg-white/5"><Plus className="w-3.5 h-3.5" />Birim ekle</button>
    </div>
  );
};

const Ihracat = ({ form, set }) => {
  const [acik, setAcik] = useState(Object.keys(form.ihracat || {}).length > 0);
  return (
    <div className="mt-2" data-testid="urun-ihracat">
      <button type="button" data-testid="urun-ihracat-ac" onClick={() => setAcik(!acik)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${acik ? "" : "-rotate-90"}`} />İhracat bilgileri (isteğe bağlı)</button>
      {acik && <div className="grid sm:grid-cols-4 gap-3 mt-3">{IHRACAT.map(([k, l, ph]) => (
        <Field key={k} label={l}><Input data-testid={`urun-ihracat-${k}`} placeholder={ph} className="bg-white/5" value={form.ihracat?.[k] || ""} onChange={(e) => set("ihracat", { ...form.ihracat, [k]: e.target.value })} /></Field>))}</div>}
    </div>
  );
};

export const urunBos = (ayar) => ({ ...BOS, kdv_orani: ayar?.varsayilan_kdv ?? 20, para_birimi: ayar?.para_birimi || "TRY" });

const Num = ({ k, form, set }) => (
  <Input data-testid={`urun-${k}`} type="number" step="any" className="bg-white/5" value={form[k]} onChange={(e) => set(k, e.target.value === "" ? 0 : Number(e.target.value))} />
);

export const UrunDialog = ({ open, onClose, initial, ayar, oznitelikler, ambalajlar, istasyonlar = [], onSaved }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOz = (k, v) => setForm((f) => ({ ...f, ozellikler: { ...f.ozellikler, [k]: v } }));
  const setAmb = (k, v) => setForm((f) => ({ ...f, ambalaj_carpanlari: { ...f.ambalaj_carpanlari, [k]: v } }));
  const uretilen = ["mamul", "yari_mamul"].includes(form.kategori_tipi);
  const hizmet = form.kategori_tipi === "hizmet";

  const save = async () => {
    try {
      const payload = { ...form, ek_birimler: (form.ek_birimler || []).filter((b) => b.birim), stok_takibi: hizmet ? false : form.stok_takibi };
      if (form.id) await api.put(`/urunler/${form.id}`, payload);
      else await api.post("/urunler", payload);
      toast.success(hizmet ? "Hizmet kaydedildi" : "Ürün kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-3xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid="urun-dialog">
        <DialogHeader><DialogTitle className="font-head">{form.id ? (hizmet ? "Hizmeti düzenle" : "Ürünü düzenle") : (hizmet ? "Yeni hizmet" : "Yeni ürün")}</DialogTitle></DialogHeader>
        {form.gorsel_yolu && <img src={`${API}/urun-gorsel/${form.id}`} alt={form.ad} data-testid="urun-gorsel" className="h-32 w-auto rounded-xl object-contain bg-white/5" />}
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Ürün Kodu / SKU *"><Input data-testid="urun-kod" className="bg-white/5" value={form.kod} onChange={(e) => set("kod", e.target.value)} /></Field>
          <Field label="Ürün Adı *" className="sm:col-span-2"><Input data-testid="urun-ad" className="bg-white/5" value={form.ad} onChange={(e) => set("ad", e.target.value)} /></Field>
          <Field label="Tip"><SelectBox testid="urun-tip" value={form.kategori_tipi} onChange={(v) => set("kategori_tipi", v)}
            options={["hammadde", "yari_mamul", "mamul", "ambalaj", "hizmet"].map((t) => ({ value: t, label: TIP_ETIKET[t] }))} /></Field>
          <Field label="Para birimi"><SelectBox testid="urun-para" value={form.para_birimi} onChange={(v) => set("para_birimi", v)} options={["TRY", "USD", "EUR"]} /></Field>
          {!hizmet && <Field label="Barkod"><Input data-testid="urun-barkod" className="bg-white/5" value={form.barkod || ""} onChange={(e) => set("barkod", e.target.value)} /></Field>}
          <Field label="Kategori"><SelectBox testid="urun-kategori" value={form.kategori} onChange={(v) => set("kategori", v)} options={[...new Set([...(ayar?.kategoriler || []), form.kategori].filter(Boolean))]} /></Field>
          <Field label="Birim"><SelectBox testid="urun-birim" value={form.birim} onChange={(v) => set("birim", v)} options={ayar?.birimler || ["adet"]} /></Field>
          <Field label="KDV %"><SelectBox testid="urun-kdv" value={String(form.kdv_orani)} onChange={(v) => set("kdv_orani", Number(v))} options={(ayar?.kdv_oranlari || [20]).map(String)} /></Field>
          {!hizmet && <Field label="Min. Stok (uyarı)"><Num k="min_stok" form={form} set={set} /></Field>}
          <Field label="Alış / Birim Maliyet"><Num k="alis_fiyati" form={form} set={set} /></Field>
          <Field label={initial?.operasyonlar?.length ? "Operasyon Maliyeti (TL, operasyonlardan otomatik)" : "Operasyon Maliyeti (birim, TL)"}>{initial?.operasyonlar?.length ? <div className="h-10 px-3 flex items-center rounded-md bg-white/5 text-sm font-mono opacity-70" data-testid="urun-op-maliyet-oto">{Number(form.operasyon_maliyet || 0).toFixed(4)}</div> : <Num k="operasyon_maliyet" form={form} set={set} />}</Field>
          <Field label="Satış Fiyatı"><Num k="satis_fiyati" form={form} set={set} /></Field>
          <Field label="Perakende fiyat (sitede, + KDV)"><Num k="perakende_fiyati" form={form} set={set} /></Field>
          <div className="flex items-end gap-6 pb-2">
            {!hizmet && <label className="flex items-center gap-2 text-sm"><Switch data-testid="urun-stok-takibi" checked={form.stok_takibi} onCheckedChange={(v) => set("stok_takibi", v)} />Stok takibi</label>}
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="urun-aktif" checked={form.aktif} onCheckedChange={(v) => set("aktif", v)} />Aktif</label>
          </div>
        </div>
        {!hizmet && <EkBirimler form={form} set={set} birimler={ayar?.birimler || ["adet"]} />}
        {!hizmet && <Ihracat form={form} set={set} />}
        {uretilen && oznitelikler.length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Sektöre özel alanlar</div>
            <div className="grid sm:grid-cols-3 gap-4">
              {oznitelikler.filter((o) => o.aktif).map((o) => (
                <Field key={o.id} label={`${o.ad}${o.zorunlu ? " *" : ""}`} className={o.tip === "arac_listesi" ? "sm:col-span-3" : ""}><DynamicField def={o} value={form.ozellikler[o.kod]} onChange={(v) => setOz(o.kod, v)} /></Field>
              ))}
            </div>
          </div>
        )}
        {uretilen && istasyonlar.length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Üretim rotası (özel ürünler için istasyon seçin)</div>
            <RotaSecici istasyonlar={istasyonlar} secili={form.rota || []} onChange={(v) => set("rota", v)} testid="urun-rota" />
          </div>
        )}
        {!hizmet && ambalajlar.length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Ambalaj çarpanları (bir üst seviyede kaç adet)</div>
            <div className="grid grid-cols-3 gap-4">
              {ambalajlar.map((a) => (
                <Field key={a.id} label={a.ad}>
                  <Input data-testid={`amb-${a.kod}`} type="number" className="bg-white/5" placeholder={String(a.carpan)}
                    value={form.ambalaj_carpanlari[a.kod] ?? ""} onChange={(e) => setAmb(a.kod, e.target.value === "" ? undefined : Number(e.target.value))} />
                </Field>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5" data-testid="urun-iptal">Vazgeç</button>
          <GradButton onClick={save} data-testid="urun-kaydet">Kaydet</GradButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
